import { z } from 'zod';

/**
 * @aiPurpose the three lifecycle phases one CRM v4 `Engagement` runs through
 *   WITHOUT being re-created (#2240 P-5 / #2241 BR-1): a лид (LEAD) becomes
 *   оформление (CLOSING) becomes сделка (DEAL). The record's `phase` changes;
 *   the row does not.
 * @aiInvariant append-only; stored on `Engagement` + `PhaseSnapshot` rows
 *   forever. Mirrored by the Prisma `EngagementPhase` enum in
 *   `apps/backend/prisma/schema/engagement.prisma` and verified by
 *   `scripts/check-enum-sync.sh`. UI/i18n say «лид»/«сделка» — never
 *   "Engagement"/"phase" literally (D-A).
 */
export enum EngagementPhase {
  LEAD = 'LEAD',
  CLOSING = 'CLOSING',
  DEAL = 'DEAL',
}
export const EngagementPhaseSchema = z.nativeEnum(EngagementPhase);

/**
 * @aiPurpose few, linear funnel stages for analytics (#2240 P-3) — operational
 *   work is tasks / checklist items INSIDE a stage, never micro-stages. M0
 *   defines the LEAD-phase stage set the DB invariants reference (INV-1 gates
 *   IN_WORK/POSTPONED on a bound client; INV-5 gates POSTPONED on `resumeAt`;
 *   INV-8 marks NON_TARGET/REFUSED terminal). CLOSING/DEAL-phase stages are
 *   appended by later milestones (M2/M4/M5) — this enum is append-only.
 * @aiInvariant append-only. Mirrored by the Prisma `EngagementStage` enum in
 *   `apps/backend/prisma/schema/engagement.prisma`; `scripts/check-enum-sync.sh`
 *   enforces parity. Never remove or rename a value.
 */
export enum EngagementStage {
  NEW = 'NEW',
  IN_WORK = 'IN_WORK',
  POSTPONED = 'POSTPONED',
  NON_TARGET = 'NON_TARGET',
  REFUSED = 'REFUSED',
}
export const EngagementStageSchema = z.nativeEnum(EngagementStage);

/**
 * @aiPurpose status of one CLOSING-phase checklist item (#2241 FR-9). Behavior
 *   (which items exist, how they gate the phase) lands in M4; M0 delivers the
 *   table + enum only.
 * @aiInvariant append-only. Mirrored by the Prisma `ChecklistItemStatus` enum
 *   in `apps/backend/prisma/schema/engagement.prisma`; parity enforced by
 *   `scripts/check-enum-sync.sh`.
 */
export enum ChecklistItemStatus {
  TODO = 'TODO',
  DONE = 'DONE',
  SKIPPED = 'SKIPPED',
}
export const ChecklistItemStatusSchema = z.nativeEnum(ChecklistItemStatus);

// ─── LEAD-phase state machine (#2243, epic #2240) ───────────────────────────

/** Terminal LEAD-phase stages — final, no transitions out (INV-8). */
export const ENGAGEMENT_TERMINAL_STAGES: readonly EngagementStage[] = [
  EngagementStage.NON_TARGET,
  EngagementStage.REFUSED,
] as const;

const TERMINAL_STAGE_SET: ReadonlySet<EngagementStage> = new Set(ENGAGEMENT_TERMINAL_STAGES);

/** True when `stage` is a terminal LEAD-phase stage (NON_TARGET / REFUSED). */
export const isTerminalEngagementStage = (stage: EngagementStage): boolean =>
  TERMINAL_STAGE_SET.has(stage);

/**
 * @aiPurpose the moves a manager may request on a LEAD-phase `Engagement`
 *   (#2243 FR-2). Four are LEAD-phase stage targets; `CLOSING` is the
 *   «договорились» PHASE hop (LEAD → CLOSING) whose CLOSING behavior lands in
 *   M4 — M2 only wires the phase advance. `NEW` is never a target (you cannot
 *   move back to the untouched intake stage).
 * @aiInvariant not a Prisma enum (a wire-only action space); the resulting
 *   `(phase, stage)` writes use the Prisma `EngagementPhase`/`EngagementStage`
 *   enums, which stay parity-checked by `scripts/check-enum-sync.sh`.
 */
export enum EngagementTransitionTarget {
  IN_WORK = 'IN_WORK',
  POSTPONED = 'POSTPONED',
  CLOSING = 'CLOSING',
  NON_TARGET = 'NON_TARGET',
  REFUSED = 'REFUSED',
}
export const EngagementTransitionTargetSchema = z.nativeEnum(EngagementTransitionTarget);

/**
 * @aiPurpose canonical LEAD- + CLOSING-phase transition graph (#2243 FR-2 /
 *   #2245 FR-4). The service mirrors NO transition logic — it defers to this
 *   predicate, then layers the INV-1/INV-5/reason gates on top. LEAD sources
 *   move per the stage edges below; CLOSING rolls back to IN_WORK (back to the
 *   LEAD phase) or REFUSED (terminal). DEAL sources are rejected (M5+).
 * @aiInvariant LEAD edges: NEW→{IN_WORK,POSTPONED,NON_TARGET,REFUSED};
 *   IN_WORK→{POSTPONED,CLOSING,NON_TARGET,REFUSED};
 *   POSTPONED→{IN_WORK,NON_TARGET,REFUSED}. CLOSING edges (#2245 FR-4):
 *   →{IN_WORK,REFUSED} — CLOSING has one working stage, so its rollback set
 *   keys on the PHASE, not `fromStage`. Self-transitions and any move out of a
 *   terminal stage return false. Client-bound / resumeAt / reason gates are
 *   enforced by the domain service, NOT here.
 */
export const canTransitionEngagement = (
  fromPhase: EngagementPhase,
  fromStage: EngagementStage,
  target: EngagementTransitionTarget,
): boolean => {
  // CLOSING-phase rollbacks (#2245 FR-4): the CLOSING checklist — not a stage —
  // drives the phase, so its two exits key on the phase alone: back to IN_WORK
  // (LEAD) or to REFUSED (terminal). The «payment confirmed» forward hop to
  // DEAL is NOT a manager transition — it is the M5 conversion, so it is absent.
  if (fromPhase === EngagementPhase.CLOSING) {
    return (
      target === EngagementTransitionTarget.IN_WORK || target === EngagementTransitionTarget.REFUSED
    );
  }
  if (fromPhase !== EngagementPhase.LEAD) return false;
  if (isTerminalEngagementStage(fromStage)) return false;
  const allowed: Readonly<Record<EngagementStage, readonly EngagementTransitionTarget[]>> = {
    [EngagementStage.NEW]: [
      EngagementTransitionTarget.IN_WORK,
      EngagementTransitionTarget.POSTPONED,
      EngagementTransitionTarget.NON_TARGET,
      EngagementTransitionTarget.REFUSED,
    ],
    [EngagementStage.IN_WORK]: [
      EngagementTransitionTarget.POSTPONED,
      EngagementTransitionTarget.CLOSING,
      EngagementTransitionTarget.NON_TARGET,
      EngagementTransitionTarget.REFUSED,
    ],
    [EngagementStage.POSTPONED]: [
      EngagementTransitionTarget.IN_WORK,
      EngagementTransitionTarget.NON_TARGET,
      EngagementTransitionTarget.REFUSED,
    ],
    [EngagementStage.NON_TARGET]: [],
    [EngagementStage.REFUSED]: [],
  };
  return allowed[fromStage].includes(target);
};

export const ENGAGEMENT_SCORE_MIN = 1;
export const ENGAGEMENT_SCORE_MAX = 5;
export const ENGAGEMENT_TRANSITION_REASON_MAX = 256;

/** Manager qualification score 1..5 (bounded, mirrors the DB CHECK). */
export const EngagementScoreSchema = z
  .number()
  .int()
  .min(ENGAGEMENT_SCORE_MIN)
  .max(ENGAGEMENT_SCORE_MAX);

/**
 * @aiPurpose input for `PATCH /api/engagements/:id/stage` (#2243). `target`
 *   picks the move; `resumeAt` is MANDATORY when target=POSTPONED (INV-5,
 *   validated in the service); `reason` is MANDATORY on a terminal target
 *   (NON_TARGET/REFUSED, FR-4). The graph + gates are enforced server-side —
 *   an illegal move rejects with a typed domain error.
 * @aiInvariant `.strict()` blocks unknown keys. The client-bound gate
 *   (INV-1/INV-3) is enforced by the service + DB CHECK, not this schema.
 */
export const TransitionEngagementInputSchema = z
  .object({
    target: EngagementTransitionTargetSchema,
    resumeAt: z.string().datetime().nullable().optional(),
    reason: z.string().trim().min(1).max(ENGAGEMENT_TRANSITION_REASON_MAX).nullable().optional(),
  })
  .strict();
export type TransitionEngagementInput = z.infer<typeof TransitionEngagementInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/engagements/:id` (#2243 FR-5) — edit the
 *   manager qualification `score`. `null` clears it. `score` never gates a
 *   transition. `.strict()` + non-empty via the single required key.
 */
export const PatchEngagementInputSchema = z
  .object({
    score: EngagementScoreSchema.nullable(),
  })
  .strict();
export type PatchEngagementInput = z.infer<typeof PatchEngagementInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/leads/:id/score` (#2317 D-1) — the by-lead
 *   card score edit against the projected engagement. Same `{ score: 1..5 |
 *   null }` shape as the native `PatchEngagementInputSchema`, re-declared under
 *   a lead-card name so the web card mutation reads self-documenting. `null`
 *   clears the score; `.strict()` blocks unknown keys.
 */
export const UpdateLeadScoreInputSchema = z
  .object({
    score: EngagementScoreSchema.nullable(),
  })
  .strict();
export type UpdateLeadScoreInput = z.infer<typeof UpdateLeadScoreInputSchema>;

/**
 * @aiPurpose wire projection of an `Engagement` returned by the stage/score
 *   mutation + detail endpoints (#2243). Non-PII: codes, phase/stage, the bound
 *   client id, score, resumeAt, assignee id, timestamps. The quarantined
 *   `intake` PII is never projected here.
 * @aiInvariant `assigneeId` is NULLABLE (#2256 M7 / FR-1): a projected legacy
 *   Lead in the Unassigned pool (`Lead.managerId = null`) mirrors to a null
 *   assignee. A natively-created Engagement always has one; the null only
 *   arises from the read-only projection.
 */
export const EngagementSummarySchema = z
  .object({
    id: z.string().uuid(),
    leadCode: z.string(),
    dealCode: z.string().nullable(),
    phase: EngagementPhaseSchema,
    stage: EngagementStageSchema,
    clientId: z.string().uuid().nullable(),
    score: EngagementScoreSchema.nullable(),
    resumeAt: z.string().datetime().nullable(),
    assigneeId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type EngagementSummary = z.infer<typeof EngagementSummarySchema>;
